import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { BRAND_BIRD_SRC } from './Pigeon';

/**
 * The app's skeleton, cut to the shape of the rendered brand bird.
 *
 * A MASKED `Skeleton` RATHER THAN A PAINTED PATH, so there is one source of truth
 * for what a loading placeholder looks like in this product. The alternative was an
 * SVG path filled with `fill-accent`, which renders identically today and would
 * quietly stop matching the moment shadcn's skeleton changed — a second
 * implementation of a look, which is the thing `ui/skeleton.tsx` exists to prevent.
 *
 * The shared sparrow avatar keeps this transitional state tied to the app's real
 * mark without promising a particular person's species or plumage.
 *
 * ITS OWN FILE BECAUSE THERE ARE TWO CALLERS NOW (docs/5 § D260806-2000). It began
 * inside `BirdPortrait`, and then the sill needed the same thing while a species'
 * GLB loads — which is the case a person actually notices, because it is a 200px
 * bird rather than a 22px one. Copying it would have been two loading looks for one
 * product.
 *
 * The mask is the brand PNG's own alpha channel, named once at module scope from
 * the single constant `Pigeon.tsx` exports. `-webkit-` is set alongside the
 * standard property for the WebKit versions that still want it; Capacitor's iOS
 * shell is modern enough not to, and it costs one line to not care.
 */
const SILHOUETTE = `url("${BRAND_BIRD_SRC}")`;

const MASK: React.CSSProperties = {
  maskImage: SILHOUETTE,
  WebkitMaskImage: SILHOUETTE,
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
};

/**
 * What stands in while a bird is being found.
 *
 * `aria-hidden`, with no `role="status"`: a bird is decoration attached to a name
 * the surface already reads out, so announcing that a decoration is loading is noise
 * on every row of a feed. `states.tsx` owns the announced loading states, and those
 * are about content.
 */
export function BirdSkeleton({
  className,
  style,
}: {
  className?: string;
  /** For a caller that has to PLACE it as well as size it — the sill stands its
   *  placeholder on the floor line rather than filling a box. */
  style?: React.CSSProperties;
}) {
  /* Square, like the capture it stands in for, and sized by the caller's box rather
     than by the artwork — so nothing moves when the real bird lands. */
  return (
    <Skeleton
      aria-hidden
      className={cn('aspect-square w-full rounded-none', className)}
      style={{ ...MASK, ...style }}
    />
  );
}
